<template>
  <AppBottomSheet v-model:open="open" :title="notification?.title ?? ''">
    <div v-if="notification" class="notification-detail tw:grid tw:gap-4">
      <NotificationResponseSummary :notification="notification" />
      <div class="tw:flex tw:items-start tw:gap-2 tw:text-muted-foreground">
        <AppIcon name="clock" size="action" class="tw:mt-0.5 tw:flex-none" />
        <div>
          <p class="tw:type-caption">{{ t('notifications.notificationDate') }}</p>
          <time :datetime="notification.eventTimeRaw" class="tw:type-secondary">{{
            notification.eventTime
          }}</time>
        </div>
      </div>
      <section v-if="notification.snapshot.medication" class="tw:grid tw:gap-3">
        <div class="tw:flex tw:items-center tw:gap-3">
          <img
            v-if="notification.snapshot.medication.image"
            :src="notification.snapshot.medication.image"
            :alt="notification.snapshot.medication.name"
            class="tw:size-20 tw:object-contain tw:flex-none tw:rounded-(--radius-note)"
          />
          <div class="tw:min-w-0 tw:wrap-anywhere">
            <h3 class="tw:type-item">{{ notification.snapshot.medication.name }}</h3>
            <p v-if="notification.snapshot.medication.dose" class="tw:type-body">
              {{ notification.snapshot.medication.dose }}
            </p>
          </div>
        </div>
        <p
          v-if="notification.snapshot.medication.note"
          class="tw:type-body tw:whitespace-pre-wrap tw:wrap-anywhere"
        >
          {{ notification.snapshot.medication.note }}
        </p>
      </section>
      <section v-else class="tw:grid tw:gap-1.5">
        <h3 v-if="historical" class="tw:type-caption tw:text-muted-foreground">
          {{ t('notifications.originalMessage') }}
        </h3>
        <p class="tw:type-body tw:whitespace-pre-line tw:wrap-anywhere">
          {{ notification.message }}
        </p>
      </section>
      <p
        v-if="notification.allowedActions.includes(NOTIFICATION_ACTION.ACKNOWLEDGED)"
        class="tw:type-body-strong"
      >
        {{ t('notifications.readConfirmationQuestion') }}
      </p>
    </div>
    <template v-if="notification" #footer>
      <p v-if="saving" role="status" class="tw:type-secondary tw:text-muted-foreground">
        {{ t('notifications.savingResponse') }}
      </p>
      <Button
        v-for="action in primaryActions"
        :key="action"
        :disabled="saving"
        @click="emit('respond', action)"
        >{{ t('notifications.actions.' + action) }}</Button
      >
      <Button v-if="!notification.allowedActions.length" @click="open = false">{{
        t('nkf.done')
      }}</Button>
    </template>
    <template
      v-if="notification?.allowedActions.includes(NOTIFICATION_ACTION.DISMISSED)"
      #secondary-actions
    >
      <Button
        variant="outline"
        :disabled="saving"
        @click="emit('respond', NOTIFICATION_ACTION.DISMISSED)"
        >{{ t('notifications.actions.DISMISSED') }}</Button
      >
    </template>
  </AppBottomSheet>
</template>
<script setup>
// 1. Import necessary components
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppBottomSheet from '@/shared/components/AppBottomSheet.vue';
import AppIcon from '@/shared/components/AppIcon.vue';
import { Button } from '@/shared/components/ui/button';
import NotificationResponseSummary from './NotificationResponseSummary.vue';
import { NOTIFICATION_ACTION, NOTIFICATION_STATE } from '../constants/notification';

// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const props = defineProps({
  notification: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['respond']);
const { t } = useI18n();

// 3. Define a reactive variable
// 4. Define methods or computed properties if needed
const historical = computed(
  () => props.notification.state !== NOTIFICATION_STATE.PENDING || props.notification.expired,
);
const primaryActions = computed(() => {
  if (!props.notification) {
    return [];
  }
  return props.notification.allowedActions.filter(
    (action) => action !== NOTIFICATION_ACTION.DISMISSED,
  );
});

// 5. VueJS Hooks
</script>
